import React, { useEffect, useState } from 'react';
import '../../styles/user/bgvCompletedCandidates.css';
import { fetchBgvCompletedCandidates } from '../../services/user/api';
import { Search } from 'lucide-react';

const BgvCompletedCandidatesPage = () => {
  const [candidates, setCandidates] = useState([]);
  const [fetchError, setFetchError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1); // 1-based UI
  const [totalElements, setTotalElements] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [entriesPerPage, setEntriesPerPage] = useState(10);

  useEffect(() => {
    loadCandidates(currentPage - 1); // backend 0-based
  }, [currentPage, entriesPerPage]);

  const loadCandidates = async (page) => {
    try {
      setLoading(true);
      const response = await fetchBgvCompletedCandidates(page, entriesPerPage);
      if (response?.data?.status) {
        const data = response.data.data;
        setCandidates(data.content || []);
        setTotalElements(data.totalElements || 0);
        setFetchError(null);
      } else {
        setFetchError(response.data.message || 'Failed to fetch data.');
      }
    } catch (error) {
      console.error('API Error:', error);
      setFetchError('An error occurred while fetching data.');
    } finally {
      setLoading(false);
    }
  };

  const totalPages = Math.ceil(totalElements / entriesPerPage);
  const startIndex = (currentPage - 1) * entriesPerPage;

  // Frontend filtering for search
  const filteredCandidates = candidates.filter((c) =>
    Object.values(c).some((val) =>
      String(val || '')
        .toLowerCase()
        .includes(search.toLowerCase())
    )
  );
 
  return (
    <>
      <p className="client-header">BGV Completed Candidates</p>
      <div className="client-wrapper">
        {fetchError && <div className="error-message">{fetchError}</div>}

        <div className="clinet-table-controls">
          <div className="entries-select">
            Show{' '}
            <select
              value={entriesPerPage}
              onChange={(e) => {
                setEntriesPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
            >
              <option value="10">10</option>
              <option value="25">25</option>
              <option value="50">50</option>
            </select>{' '}
            entries
          </div>

          <div className="client-search-box">
            <div className="client-search-input-wrapper">
              <input
                id="client-search-input"
                type="text"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setCurrentPage(1); // ✅ reset to first page instead of 0
                }}
                placeholder="Search..."
              />
              <Search className="client-search-icon" size={18} />
            </div>
          </div>
        </div>

        <div className="clinet-table-controls">
          {loading ? (
            <div className="loading">Loading...</div>
          ) : (
            <table className="candidates-table">
              <thead>
                <tr>
                  <th>Unique ID</th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Mobile Number</th>
                  <th>DOB</th>
                  <th>Case Initiated Date</th>
                  <th>BGV Completed Date</th>
                </tr>
              </thead>
              <tbody>
                {filteredCandidates.length > 0 ? (
                  filteredCandidates.map((c) => (
                    <tr key={c.candidateid}>
                      <td>{c.candidateid}</td>
                      <td>{c.fullname}</td>
                      <td>{c.email}</td>
                      <td>{c.mobileNumber}</td>
                      <td>{c.dateOfBirth}</td>
                      <td>{c.caseInitiatedDateOnlyAsString || 'N/A'}</td>
                      <td>{c.bgcCompletedDateOnly || 'N/A'}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="7" className="no-data">
                      No matching candidates found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>

        {totalPages > 1 && (
          <div className="pagination">
            <span>
              Showing {startIndex + 1} to{' '}
              {Math.min(startIndex + entriesPerPage, totalElements)} of {totalElements} entries
            </span>
            <div>
              <button
                className="page-btn"
                onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                disabled={currentPage === 1}
              >
                Previous
              </button>
              {[...Array(totalPages)].map((_, i) => (
                <button
                  key={i}
                  className={`page-btn ${currentPage === i + 1 ? 'active' : ''}`}
                  onClick={() => setCurrentPage(i + 1)}
                >
                  {i + 1}
                </button>
              ))}
              <button
                className="page-btn"
                onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
                disabled={currentPage === totalPages}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
};
 
export default BgvCompletedCandidatesPage;